// Qué tiene que decir una pantalla que pagina mientras le faltan páginas (ESTANDAR-TABLAS §5.4).
//
// La tabla pagina en el servidor y filtra, ordena y cuenta en el navegador sobre lo que ya llegó.
// Mientras queden páginas, ninguno de esos números es un total: los rótulos de aquí lo declaran, y
// cuando ya no quedan se caen solos. Un «Cargar más» sin este rótulo es un tope silencioso.
//
// La regla se escribe UNA vez y se parametriza por el sustantivo de cada pantalla.

/** El sustantivo de las filas: lo único que cambia entre una pantalla y otra. */
export interface LoadedNoun {
    /** «crédito» */
    singular: string;
    /** «créditos» */
    plural: string;
    /** Manda en el artículo y en el participio: «los créditos cargados» vs «las evaluaciones cargadas». */
    gender: 'masculine' | 'feminine';
}

export const CUSTOMERS_NOUN: LoadedNoun = {
    singular: 'cliente',
    plural: 'clientes',
    gender: 'masculine',
};

export const CREDITS_NOUN: LoadedNoun = {
    singular: 'crédito',
    plural: 'créditos',
    gender: 'masculine',
};

export const DEVICES_NOUN: LoadedNoun = {
    singular: 'equipo',
    plural: 'equipos',
    gender: 'masculine',
};

export const INSTALLMENTS_NOUN: LoadedNoun = {
    singular: 'cuota',
    plural: 'cuotas',
    gender: 'feminine',
};

export const PRODUCTS_NOUN: LoadedNoun = {
    singular: 'producto',
    plural: 'productos',
    gender: 'masculine',
};

export const SALES_NOUN: LoadedNoun = {
    singular: 'venta',
    plural: 'ventas',
    gender: 'feminine',
};

export const EVALUATIONS_NOUN: LoadedNoun = {
    singular: 'evaluación',
    plural: 'evaluaciones',
    gender: 'feminine',
};

/** Sobre cuánto se está contando, y si eso es todo o solo lo que llegó hasta ahora. */
export interface LoadedScope {
    /** Filas acumuladas de todas las páginas traídas. */
    loaded: number;
    /** Quedan páginas por traer: nada de lo que se cuente aquí es un total. */
    hasMore: boolean;
}

/** Las piezas del rótulo ya concordadas en género y número con el sustantivo de la pantalla. */
interface Agreement {
    /** «los 75 créditos cargados» · «el crédito cargado» */
    definitePhrase: string;
    /** «75 créditos cargados» · «1 crédito cargado» */
    countedPhrase: string;
    /** «esos» · «esa» — lo ya cargado, referido de vuelta. */
    demonstrative: string;
    /** «un crédito» · «una evaluación» */
    indefinite: string;
}

/** Concuerda las piezas del rótulo. Un solo lugar decide «cargados», «cargada», «ese» y «una». */
function agree(count: number, noun: LoadedNoun): Agreement {
    const one = count === 1;
    const masculine = noun.gender === 'masculine';
    const article = one ? (masculine ? 'el' : 'la') : masculine ? 'los' : 'las';
    const participle = `cargad${masculine ? 'o' : 'a'}${one ? '' : 's'}`;
    const word = one ? noun.singular : noun.plural;
    return {
        definitePhrase: one
            ? `${article} ${word} ${participle}`
            : `${article} ${String(count)} ${word} ${participle}`,
        countedPhrase: `${String(count)} ${word} ${participle}`,
        demonstrative: one ? (masculine ? 'ese' : 'esa') : masculine ? 'esos' : 'esas',
        indefinite: `${masculine ? 'un' : 'una'} ${noun.singular}`,
    };
}

/** Cuenta con su sustantivo: «1 crédito» · «44 créditos». */
export function countWithNoun(count: number, noun: LoadedNoun): string {
    return `${String(count)} ${count === 1 ? noun.singular : noun.plural}`;
}

/** El número de una pastilla: solo, o «12 de 75» mientras la lista siga incompleta. */
export function loadedCountLabel(count: number, scope: LoadedScope): string {
    if (!scope.hasMore) return String(count);
    return `${String(count)} de ${String(scope.loaded)}`;
}

/**
 * Lo que la pastilla explica al pasar el puntero: qué cuenta y sobre qué universo. Sin páginas
 * pendientes queda el texto de la pastilla a secas, que puede no existir.
 */
export function loadedCountTitle(base: string, scope: LoadedScope, noun: LoadedNoun): string;
export function loadedCountTitle(
    base: string | undefined,
    scope: LoadedScope,
    noun: LoadedNoun,
): string | undefined;
export function loadedCountTitle(
    base: string | undefined,
    scope: LoadedScope,
    noun: LoadedNoun,
): string | undefined {
    if (!scope.hasMore) return base;
    const { definitePhrase } = agree(scope.loaded, noun);
    const scoped = `Cuenta solo ${definitePhrase}; todavía falta traer el resto.`;
    return base === undefined ? scoped : `${base}. ${scoped}`;
}

/** El aviso bajo la barra de pastillas. `null` cuando ya no quedan páginas y los números son totales. */
export function loadedCountsNote(scope: LoadedScope, noun: LoadedNoun): string | null {
    if (!scope.hasMore) return null;
    const { countedPhrase, demonstrative } = agree(scope.loaded, noun);
    return `Llevas ${countedPhrase}. Los conteos, los filtros y la búsqueda solo miran ${demonstrative}; usa «Cargar más» al pie de la tabla para traer el resto.`;
}

/** El aviso del panel de filtros: la búsqueda y los filtros miran lo cargado, no el listado entero. */
export function loadedFiltersNote(scope: LoadedScope, noun: LoadedNoun): string | null {
    if (!scope.hasMore) return null;
    const { countedPhrase, demonstrative, indefinite } = agree(scope.loaded, noun);
    return `Llevas ${countedPhrase}. Los filtros y la búsqueda solo miran ${demonstrative}: ${indefinite} que todavía no se cargó no aparece aunque coincida; usa «Cargar más» al pie de la tabla para traer el resto.`;
}

/**
 * El contador sobre la tabla, que lleva dos cosas distintas encima. «de 75 cargados» es el
 * calificador de paginación y se cae al agotarse las páginas; «44 de 300» es la razón del filtro y
 * se queda mientras el filtro esconda filas.
 */
export function loadedResultLabel(shown: number, scope: LoadedScope, noun: LoadedNoun): string {
    if (scope.hasMore) return `${String(shown)} de ${agree(scope.loaded, noun).countedPhrase}`;
    if (shown === scope.loaded) return countWithNoun(shown, noun);
    return `${String(shown)} de ${countWithNoun(scope.loaded, noun)}`;
}

/** Lo que el botón que trae la siguiente página del servidor explica al pasar el puntero. */
export function loadMoreNote(noun: LoadedNoun): string {
    return `Trae la siguiente página de ${noun.plural} del servidor y la suma a la lista. Los conteos, los filtros y el orden se recalculan sobre todo lo cargado.`;
}

export const LOAD_MORE_LABEL = 'Cargar más';

export const LOADING_MORE_LABEL = 'Cargando…';

/** La página anterior falló: el botón invita a repetirla, no a seguir avanzando. */
export const RETRY_LOAD_MORE_LABEL = 'Reintentar';

/** Cierra el motivo del silo, que llega sin puntuación garantizada, para no pegar dos frases. */
export function closeSentence(message: string): string {
    const trimmed = message.trim();
    return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * Por qué falló la SIGUIENTE página, con el motivo del silo tal cual: es lo único que distingue un
 * 403 de una caída. Lo que ya llegó no se tira, y el texto lo dice para que nadie recargue de más.
 */
export function nextPageFailureNote(reason: string, noun: LoadedNoun): string {
    const article = noun.gender === 'masculine' ? 'Los' : 'Las';
    return `No se pudieron traer más ${noun.plural}: ${closeSentence(reason)} ${article} que ya ves siguen aquí; vuelve a intentarlo.`;
}

/** El contador del pie: la cuenta cuando hay lista, y por qué no la hay cuando todavía falta. */
export function footerCountLabel(
    shown: number | null,
    scope: LoadedScope | null,
    noun: LoadedNoun,
    readFailed = false,
): string {
    if (shown === null || scope === null) {
        return readFailed ? 'Lectura fallida' : `Cargando ${noun.plural}…`;
    }
    return loadedResultLabel(shown, scope, noun);
}
